import React, { FC, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { SingleValue } from 'react-select';
import { FormControl } from 'src/components/widgets/formControl';
import { Checkbox } from 'src/components/widgets/checkbox';
import { AdvancedSelect } from 'src/components/widgets/select';
import { TextArea } from 'src/components/widgets/textArea';
import UsageControls, { UsageControlsRef } from 'src/components/widgets/usageControls/usageControls';
import { OptionType, OptionWithSubLabel } from 'src/shared/globals';
import { T } from 'src/shared/helpers';
import { useValidationContext, validateRequiredTrimmed, ValidationStateProvider } from 'src/shared/validation';
import styled from 'styled-components';
import { TOOL_DEFINITIONS } from '../common/constants';
import { InfoCallout, SectionCard, SectionCardTitle, StackedBody, StackedBreadcrumb } from '../common/styled';
import type { aiDesignGenEditor_CreateOrUpdateMutation } from './__generated__/aiDesignGenEditor_CreateOrUpdateMutation.graphql';
import type { aiDesignGenEditor_Query } from './__generated__/aiDesignGenEditor_Query.graphql';
import { PanelLinesSkeleton } from '../common/skeletons';

const CompactControls = styled.div`
    font-size: 12px;
    label,
    span,
    input,
    select {
        font-size: 12px;
    }
`;

const CharCounter = styled.div<{ $isOverLimit: boolean }>`
    text-align: right;
    font-size: 12px;
    color: ${(props) => (props.$isOverLimit ? '#d32f2f' : '#666')};
    margin-top: 5px;
`;

export interface AIDesignGenEditorProps {
    itemId: string;
    productId: string;
    toolKey: string;
    onBack: () => void;
    onSave: () => void;
    saveRef: React.MutableRefObject<(() => Promise<void>) | null>;
    onCanSaveChange?: (canSave: boolean) => void;
}

type PresetOption = {
    value: string;
    label: string;
    subLabel: string;
    isCustom: boolean;
};

const CUSTOM_OPTION_VALUE = '-1';
const MAX_CUSTOM_PROMPT_LENGTH = 10000;

const AIDesignGenEditorContent: FC<AIDesignGenEditorProps & { fetchKey: string }> = ({
    productId,
    toolKey,
    onBack,
    onSave,
    saveRef,
    onCanSaveChange,
    fetchKey
}) => {
    const toolName = TOOL_DEFINITIONS.find((tool) => tool.key === toolKey)?.name || '';
    const usageControlsRef = useRef<UsageControlsRef>(null);
    const validationContext = useValidationContext();
    const productNameData = useLazyLoadQuery<any>(
        graphql`
            query aiDesignGenEditor_ProductNameQuery($id: ID!) {
                node(id: $id) {
                    ... on Model {
                        name
                    }
                }
            }
        `,
        { id: productId }
    );
    const productName = (productNameData?.node as any)?.name || '';

    const data = useLazyLoadQuery<aiDesignGenEditor_Query>(
        graphql`
            query aiDesignGenEditor_Query {
                designWithAISetting {
                    id
                    settingsID
                    presetID
                    customPrompt
                    useAIDesignCreatorMode
                }
                designWithAIPresets {
                    id
                    presetID
                    name
                    description
                    isActive
                }
            }
        `,
        {},
        { fetchPolicy: 'network-only', fetchKey }
    );

    const existing = data.designWithAISetting ?? null;

    const presetOptions: PresetOption[] = [
        ...(data.designWithAIPresets || [])
            .filter((preset) => preset.isActive)
            .map((preset) => ({
                value: preset.presetID.toString(),
                label: preset.name,
                subLabel: preset.description || '',
                isCustom: false
            })),
        {
            value: CUSTOM_OPTION_VALUE,
            label: T._('Custom AI Behavior', 'Admin'),
            subLabel: T._('Define custom instructions to control how the AI interacts with customers.', 'Admin'),
            isCustom: true
        }
    ];

    const [selectedPreset, setSelectedPreset] = useState<SingleValue<PresetOption>>(null);
    const [customPromptText, setCustomPromptText] = useState('');
    const [useAIDesignCreatorMode, setUseAIDesignCreatorMode] = useState(existing?.useAIDesignCreatorMode ?? false);
    const [usageControlsValid, setUsageControlsValid] = useState(true);

    useEffect(() => {
        if (existing) {
            if (existing.customPrompt) {
                setSelectedPreset(presetOptions.find((opt) => opt.value === CUSTOM_OPTION_VALUE) || null);
                setCustomPromptText(existing.customPrompt);
            } else if (existing.presetID) {
                setSelectedPreset(presetOptions.find((opt) => opt.value === existing.presetID?.toString()) || null);
                setCustomPromptText('');
            }
        } else {
            setSelectedPreset(presetOptions.find((opt) => opt.value === '2') || null);
            setCustomPromptText('');
        }
    }, []);

    const [commitSave] = useMutation<aiDesignGenEditor_CreateOrUpdateMutation>(graphql`
        mutation aiDesignGenEditor_CreateOrUpdateMutation($presetID: Int, $customPrompt: String, $useAIDesignCreatorMode: Boolean!) {
            createOrUpdateDesignWithAISetting(presetID: $presetID, customPrompt: $customPrompt, useAIDesignCreatorMode: $useAIDesignCreatorMode) {
                id
                settingsID
                presetID
                customPrompt
                useAIDesignCreatorMode
            }
        }
    `);

    const handleSave = useCallback(async () => {
        if (!validationContext.submit()) return;

        if (usageControlsRef.current?.hasChanges()) {
            usageControlsRef.current.save();
        }
        const isCustom = selectedPreset?.isCustom;
        await new Promise<void>((resolve, reject) => {
            commitSave({
                variables: {
                    presetID: isCustom ? null : selectedPreset ? parseInt(selectedPreset.value) : null,
                    customPrompt: isCustom ? customPromptText : null,
                    useAIDesignCreatorMode
                },
                onCompleted: () => {
                    onSave();
                    resolve();
                },
                onError: (error) => reject(error)
            });
        });
    }, [selectedPreset, customPromptText, useAIDesignCreatorMode, onSave, commitSave, validationContext]);

    saveRef.current = handleSave;

    useEffect(() => {
        onCanSaveChange?.(!!selectedPreset && usageControlsValid);
    }, [selectedPreset, usageControlsValid, onCanSaveChange]);

    const handlePresetChange = (newValue: SingleValue<OptionType>) => {
        const preset = presetOptions.find((opt) => opt.value === newValue?.value);
        if (!preset) return;
        setSelectedPreset(preset);
        if (preset.isCustom) {
            setCustomPromptText(existing?.customPrompt || '');
        } else {
            setCustomPromptText('');
        }
    };

    return (
        <>
            <StackedBreadcrumb>
                <span onClick={onBack}>{productName}</span> / {toolName}
            </StackedBreadcrumb>
            <StackedBody>
                <InfoCallout>{T._('This tool applies to all products.', 'Admin')}</InfoCallout>

                <SectionCard>
                    <SectionCardTitle>{T._('Interaction Mode', 'Admin')}</SectionCardTitle>
                    <FormControl label={T._('Interaction mode', 'Admin')}>
                        <AdvancedSelect<OptionType>
                            value={selectedPreset}
                            onChange={handlePresetChange}
                            options={presetOptions}
                            placeholder={T._('Select a prompt preset', 'Admin')}
                            components={{ Option: OptionWithSubLabel }}
                            isSearchable={false}
                        />
                    </FormControl>

                    {selectedPreset?.isCustom && (
                        <FormControl
                            label={T._('Custom AI Behavior', 'Admin')}
                            caption={T._('Define custom instructions to control how the AI interacts with customers.', 'Admin')}
                        >
                            <TextArea
                                value={customPromptText}
                                onChange={(e) => setCustomPromptText(e.target.value)}
                                rows={6}
                                maxLength={MAX_CUSTOM_PROMPT_LENGTH}
                                placeholder={T._('Enter your custom AI prompt here...', 'Admin')}
                                validationKey='productAiDesignCustomPrompt'
                                validationLabel={T._('Custom AI Behavior', 'Admin')}
                                validate={(value) => selectedPreset?.isCustom === true && validateRequiredTrimmed(value)}
                                validationMessage={T._('Custom AI Behavior is required.', 'Admin')}
                            />
                            <CharCounter $isOverLimit={customPromptText.length > MAX_CUSTOM_PROMPT_LENGTH}>
                                {customPromptText.length} / {MAX_CUSTOM_PROMPT_LENGTH}
                            </CharCounter>
                        </FormControl>
                    )}
                </SectionCard>

                <SectionCard>
                    <SectionCardTitle>{T._('UI Behaviour', 'Admin')}</SectionCardTitle>
                    <FormControl>
                        <Checkbox
                            variant='ai'
                            checked={useAIDesignCreatorMode}
                            onChange={setUseAIDesignCreatorMode}
                            label={T._('Start in AI Creator mode', 'Admin')}
                            caption={T._('Open the Customizer with the AI-first landing and conversational editor.', 'Admin')}
                        />
                    </FormControl>
                </SectionCard>

                <SectionCard>
                    <SectionCardTitle>{T._('Usage Controls', 'Admin')}</SectionCardTitle>
                    <CompactControls>
                        <Suspense fallback={null}>
                            <UsageControls ref={usageControlsRef} toolType='CustomizerDesignGenerator' onValidChange={setUsageControlsValid} />
                        </Suspense>
                    </CompactControls>
                </SectionCard>
            </StackedBody>
        </>
    );
};

export const AIDesignGenEditor: FC<AIDesignGenEditorProps> = (props) => {
    // Per-mount fetchKey: Relay dedupes active queries within a TTL window after unmount,
    // so navigating away and returning quickly would serve cached data even with network-only.
    // Declared here (above the Suspense boundary) so the initializer isn't re-run on suspend retries.
    const [fetchKey] = useState(() => Date.now().toString());
    return (
        <ValidationStateProvider>
            <Suspense
                fallback={
                    <>
                        <StackedBreadcrumb>
                            <span onClick={props.onBack}>{T._('Loading...', 'Admin')}</span>
                        </StackedBreadcrumb>
                        <StackedBody>
                            <PanelLinesSkeleton />
                        </StackedBody>
                    </>
                }
            >
                <AIDesignGenEditorContent {...props} fetchKey={fetchKey} />
            </Suspense>
        </ValidationStateProvider>
    );
};
